/**
 * WHO THIS TV IS, told to a phone it dials. A TV that PULLS from a phone (Samsung Tizen — lanDiscovery — and
 * any TV whose own scan finds a phone's :7780) reaches a phone that asks its user first ("<name> wants to
 * show your visuals" — Allow / Not now) and remembers the answer. Without an identity the phone can only
 * say "a TV" and key the answer to the TV's LAN address, which DHCP changes. So every dial this page makes
 * to a sender opens with one text frame:
 *
 *   {"receiver":{"id":"<stable id>","name":"<display name>","caps":["calm"]}}      (name optional)
 *
 * `caps` — what this receiver HONOURS of the sender's levels, so the phone promises only what this TV does:
 *  - "calm": the phone's "Calm flashes" — this page's VisualHost limits the finished frame of every world
 *    (renderLoop: host.setCalm). Every web receiver has it; a receiver that doesn't list it gets no promise.
 *
 * Phones read it (iOS CastServer / Android CastServer + TvConsent), key the remembered answer by `id` and
 * show `name`; a dial without it (an older TV) falls back to the address and the router's name for it.
 * Senders that don't read inbound frames (desktop, the TV relays' local sinks) ignore it.
 *
 * The id: the host shell's (window.__syqelDevice.id — Tizen hashes the TV's DUID, so it survives a reinstall),
 * else one generated here and kept in localStorage; the name: the shell's (window.__syqelDevice.name — e.g.
 * "Samsung TV QN65Q80T"), else none (the phone names it).
 */
interface HostDevice { id?: unknown; name?: unknown }

const ID_KEY = 'syqel.receiver.id'
const ID_RE = /^[A-Za-z0-9_-]{6,64}$/
let sessionId = '' // when storage is unavailable: stable for this page's life at least

function generatedId(): string {
  try {
    const saved = localStorage.getItem(ID_KEY)
    if (saved && ID_RE.test(saved)) return saved
  } catch { /* no storage */ }
  if (!sessionId) {
    const b = new Uint8Array(12)
    try { crypto.getRandomValues(b) } catch { for (let i = 0; i < b.length; i++) b[i] = Math.floor(Math.random() * 256) }
    sessionId = 'tv-' + Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  }
  try { localStorage.setItem(ID_KEY, sessionId) } catch { /* no storage */ }
  return sessionId
}

/** This TV's identity for a phone: { id, name? }. */
export function receiverIdentity(): { id: string; name?: string } {
  const host = (window as unknown as { __syqelDevice?: HostDevice }).__syqelDevice
  const id = typeof host?.id === 'string' && ID_RE.test(host.id) ? host.id : generatedId()
  const name = typeof host?.name === 'string' ? host.name.trim().slice(0, 64) : ''
  return name ? { id, name } : { id }
}

/** What this receiver honours of a sender's levels (see the header) — the web receiver's flash limiter. */
export const RECEIVER_CAPS = ['calm'] as const

/** The first frame of every dial to a sender (see the header). */
export function receiverHello(): string {
  return JSON.stringify({ receiver: { ...receiverIdentity(), caps: RECEIVER_CAPS } })
}
